import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuZap, LuTriangleAlert, LuLock, LuArrowRight, LuX } from 'react-icons/lu';
import billingService from '../services/billingService';
import './TrialCountdownBanner.css';

const TrialCountdownBanner = ({ onOpenPaywall }) => {
  const navigate = useNavigate();
  const [trialStatus, setTrialStatus] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('uw_trial_banner_dismissed') === 'true';
    } catch {
      return false;
    }
  });

  const fetchTrialStatus = useCallback(async () => {
    try {
      const data = await billingService.getTrialStatus();
      setTrialStatus(data);
    } catch (err) {
      // Non-blocking: fail silently if offline or unauthorized
    }
  }, []);

  useEffect(() => {
    fetchTrialStatus();
    const handleUpdate = () => fetchTrialStatus();
    window.addEventListener('subscriptionUpdated', handleUpdate);
    window.addEventListener('trialUpdated', handleUpdate);
    return () => {
      window.removeEventListener('subscriptionUpdated', handleUpdate);
      window.removeEventListener('trialUpdated', handleUpdate);
    };
  }, [fetchTrialStatus]);

  if (!trialStatus) return null;

  const {
    paywall_state,
    days_remaining,
    hours_remaining,
    installed_apps_count,
    total_tracked_hours,
  } = trialStatus;

  // No banner needed for paid organizations or non-trial free users without active trial
  if (paywall_state === 'paid' || paywall_state === 'free') {
    return null;
  }

  const handleAction = () => {
    if (onOpenPaywall) {
      onOpenPaywall(trialStatus);
    } else {
      navigate('/billing');
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem('uw_trial_banner_dismissed', 'true');
    } catch {}
  };

  // State 1: Active Trial (Days 1–9) - Dismissible
  if (paywall_state === 'active_trial') {
    if (dismissed) return null;
    return (
      <div className="trial-banner trial-banner-active" role="alert">
        <div className="trial-banner-left">
          <div className="trial-banner-badge">
            <LuZap className="trial-banner-icon" />
            <span>Trial Active</span>
          </div>
          <p className="trial-banner-text">
            <strong>{days_remaining ?? 14} days left</strong> on your Pro trial.
          </p>
          <div className="trial-banner-milestones">
            <span className={`milestone-tag ${installed_apps_count >= 3 ? 'completed' : ''}`}>
              {installed_apps_count}/3 Trackers
            </span>
            <span className={`milestone-tag ${total_tracked_hours >= 100 ? 'completed' : ''}`}>
              {Math.round(total_tracked_hours)}/100h Logged
            </span>
          </div>
        </div>
        <div className="trial-banner-right">
          <button type="button" className="trial-banner-btn" onClick={handleAction}>
            Upgrade to Pro <LuArrowRight className="trial-btn-icon" />
          </button>
          <button
            type="button"
            className="trial-banner-close"
            onClick={handleDismiss}
            aria-label="Dismiss banner"
          >
            <LuX />
          </button>
        </div>
      </div>
    );
  }

  // State 2: Expiring Soon (Days 10–13) - Urgent, Non-dismissible
  if (paywall_state === 'expiring_soon') {
    const daysLabel = days_remaining === 1 ? '1 day' : `${days_remaining} days`;
    return (
      <div className="trial-banner trial-banner-urgent" role="alert">
        <div className="trial-banner-left">
          <div className="trial-banner-badge urgent-badge">
            <LuTriangleAlert className="trial-banner-icon pulse-icon" />
            <span>Trial Ending</span>
          </div>
          <p className="trial-banner-text">
            <strong>⚠️ {daysLabel} ({hours_remaining}h) remaining!</strong> Keep automated screenshots, timelapse reels, and payroll access uninterrupted.
          </p>
        </div>
        <div className="trial-banner-right">
          <button type="button" className="trial-banner-btn urgent-btn" onClick={handleAction}>
            Upgrade Now <LuArrowRight className="trial-btn-icon" />
          </button>
        </div>
      </div>
    );
  }

  // State 3: Trial Expired (Day 14+) - Critical
  if (paywall_state === 'expired') {
    return (
      <div className="trial-banner trial-banner-expired" role="alert">
        <div className="trial-banner-left">
          <div className="trial-banner-badge expired-badge">
            <LuLock className="trial-banner-icon" />
            <span>Trial Expired</span>
          </div>
          <p className="trial-banner-text">
            Your 14-day free trial has concluded. Upgrade your subscription to restore full workspace capabilities.
          </p>
        </div>
        <div className="trial-banner-right">
          <button type="button" className="trial-banner-btn expired-btn" onClick={handleAction}>
            Choose Plan <LuArrowRight className="trial-btn-icon" />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default TrialCountdownBanner;
